import { ReactNode, useState } from 'react';

import { fromApiBoolean } from '@liquidweb-dev/club-sauce';
import ArrowLeftRightIcon from '@liquidweb-dev/undercurrent/icons/lucide/ArrowLeftRight';
import ChevronDownIcon from '@liquidweb-dev/undercurrent/icons/lucide/ChevronDown';
import PlusIcon from '@liquidweb-dev/undercurrent/icons/lucide/Plus';
import ListItemIcon from '@liquidweb-dev/undercurrent/ListItemIcon';
import ListItemText from '@liquidweb-dev/undercurrent/ListItemText';
import Menu from '@liquidweb-dev/undercurrent/Menu';
import MenuItem from '@liquidweb-dev/undercurrent/MenuItem';
import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import Tab from '@liquidweb-dev/undercurrent/Tab';
import Tabs from '@liquidweb-dev/undercurrent/Tabs';
import {
	Redirect,
	Link as RouterLink,
	useLocation,
	useParams,
} from 'react-router-dom';

import SuspenseFallback from 'components/routers/RouteHandler/SuspenseFallback';
import DialogAddSsl from 'components/standard/DialogAddSsl';
import DialogTransferDomain from 'components/standard/DialogTransferDomain';
import { ActionsProvider } from 'components/standard/DomainList';
import ActionsMenu from 'components/standard/DomainList/ActionsMenu';
import ErrorState from 'components/standard/ErrorState';
import PageLayout, {
	PageLayoutInsetContent,
} from 'components/standard/PageLayout';
import TabBar from 'components/standard/TabBar';
import { useNavTitle } from 'containers/LeftHandLayout';
import useCdnDomains from 'modules/cloudflare/useCdnDomains';
import { getDomainStatus } from 'modules/domains/statuses';
import useDomainList from 'modules/queries/domain/useList';
import Attributes from './Attributes';

const UpsellTabLabel = (props: Omit<StackProps, 'sx'>) => (
	<Stack
		sx={({ vars }) => ({
			flexDirection: 'row',
			alignItems: 'center',
			gap: 100,
			color: vars.palette.uc.highlight.text.main,
		})}
		{...props}
	/>
);

export interface DomainDetailsLayoutProps {
	children: ReactNode;
}

const DomainDetailsLayout = ({ children }: DomainDetailsLayoutProps) => {
	const { domain } = useParams<{ domain: string }>();
	const { pathname } = useLocation();
	useNavTitle('Domain details');

	const [isAddSslDialogOpen, setIsAddSslDialogOpen] = useState(false);
	const [isTransferDomainDialogOpen, setIsTransferDomainDialogOpen] =
		useState(false);
	const [registrationMenuAnchorEl, setRegistrationMenuAnchorEl] =
		useState<HTMLElement | null>(null);

	const {
		data: domainList,
		isLoading: isDomainListLoading,
		isError,
		refetch,
	} = useDomainList({
		domain,
		group_subdomains: true,
	});

	const { cdnDomains, isLoading: isCdnDomainsLoading } = useCdnDomains();

	// need to know if the domain exists before anything else
	if (isDomainListLoading) {
		return (
			<PageLayout title={domain}>
				<SuspenseFallback />
			</PageLayout>
		);
	}

	// handle if the api failed for whatever reason
	if (isError) {
		return (
			<PageLayout title={domain}>
				<Stack
					sx={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center' }}
				>
					<ErrorState onTryAgain={refetch} />
				</Stack>
			</PageLayout>
		);
	}

	const domainDetails = domainList?.items[0] || {};

	const hasRegistration = Boolean(domainDetails.dreg);
	const dnsArray = Array.isArray(domainDetails.dns) ? domainDetails.dns : [];
	const sslArray = Array.isArray(domainDetails.ssl) ? domainDetails.ssl : [];
	const hasDns = dnsArray.length > 0;
	const hasSsl = sslArray.length > 0;
	const hasWildcardSsl = sslArray.some(({ wildcard }) =>
		fromApiBoolean(wildcard),
	);

	// redirect to domain list if no domain objects exist
	if (!hasRegistration && !hasDns && !hasSsl) {
		return <Redirect to="/domain" />;
	}

	// the tabs correspond to the portion of the path after the domain name
	const tabsValue = pathname.split('/')[3] || 'registration';

	// find a tab to land on
	const tabs: string[] = [];
	if (hasRegistration) tabs.push('registration');
	if (hasDns) tabs.push('dns');
	if (hasSsl) tabs.push('ssl');

	const shouldRedirect =
		tabsValue !== 'dns' && // the DNS tab always exists, but we don't want to redirect there if the user doesn't have DNS
		!tabs.includes(tabsValue);

	if (shouldRedirect) {
		return <Redirect to={`/domain/${domain}/${tabs[0]}`} />;
	}

	const domainStatus = getDomainStatus(domainDetails);
	const canRenewDomain = fromApiBoolean(domainDetails.dreg?.renewable);
	const dregUniqId = domainDetails.dreg?.uniq_id;

	return (
		<PageLayout
			variant="dynamic"
			title={domain}
			attributesSlot={
				<Attributes
					domainAutoRenew={fromApiBoolean(domainDetails.dreg?.autorenew)}
					domainExpirationDate={domainDetails.dreg?.expiration}
					domainStatus={domainStatus}
					dnsZones={dnsArray.length}
					ssls={sslArray.length}
					sslWildcard={hasWildcardSsl}
				/>
			}
			actionsSlot={
				<ActionsProvider
					domain={domain}
					canRenewDomain={canRenewDomain}
					dregUniqId={dregUniqId}
				>
					<ActionsMenu
						item={domainDetails}
						cdnDomains={cdnDomains}
						disabled={isCdnDomainsLoading}
					/>
				</ActionsProvider>
			}
		>
			<TabBar sx={{ marginBottom: 600 }}>
				<Tabs value={tabsValue} variant="scrollable">
					{hasRegistration && (
						<Tab
							component={RouterLink}
							value="registration"
							to={`/domain/${domain}`}
							label="Registration"
						/>
					)}
					{hasDns && (
						<Tab
							component={RouterLink}
							value="dns"
							to={`/domain/${domain}/dns`}
							label="DNS"
						/>
					)}
					{hasSsl && (
						<Tab
							component={RouterLink}
							value="ssl"
							to={`/domain/${domain}/ssl`}
							label="SSL"
						/>
					)}

					{!hasRegistration && (
						<Tab
							onClick={(e) => setRegistrationMenuAnchorEl(e.currentTarget)}
							label={
								<UpsellTabLabel>
									<PlusIcon />
									Add registration
									<ChevronDownIcon />
								</UpsellTabLabel>
							}
						/>
					)}
					{!hasDns && (
						<Tab
							component={RouterLink}
							value="dns"
							to={`/domain/${domain}/dns`}
							label={
								<UpsellTabLabel>
									<PlusIcon />
									Add DNS
								</UpsellTabLabel>
							}
						/>
					)}
					{!hasSsl && (
						<Tab
							onClick={() => setIsAddSslDialogOpen(true)}
							label={
								<UpsellTabLabel>
									<PlusIcon />
									Add SSL
								</UpsellTabLabel>
							}
						/>
					)}
				</Tabs>
			</TabBar>
			<PageLayoutInsetContent sx={{ flexGrow: 1 }}>
				{children}
			</PageLayoutInsetContent>

			{!hasSsl && (
				<DialogAddSsl
					open={isAddSslDialogOpen}
					onClose={() => setIsAddSslDialogOpen(false)}
				/>
			)}
			{!hasRegistration && (
				<>
					<Menu
						open={Boolean(registrationMenuAnchorEl)}
						anchorEl={registrationMenuAnchorEl}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
						transformOrigin={{ vertical: 'top', horizontal: 'center' }}
						onClose={() => setRegistrationMenuAnchorEl(null)}
					>
						<MenuItem
							component={RouterLink}
							to={`/shop/domain?name=${domain}`}
							onClick={() => setRegistrationMenuAnchorEl(null)}
						>
							<ListItemIcon>
								<PlusIcon />
							</ListItemIcon>
							<ListItemText>Register new</ListItemText>
						</MenuItem>
						<MenuItem
							onClick={() => {
								setRegistrationMenuAnchorEl(null);
								setIsTransferDomainDialogOpen(true);
							}}
						>
							<ListItemIcon>
								<ArrowLeftRightIcon />
							</ListItemIcon>
							<ListItemText>Transfer existing</ListItemText>
						</MenuItem>
					</Menu>
					<DialogTransferDomain
						open={isTransferDomainDialogOpen}
						onClose={() => setIsTransferDomainDialogOpen(false)}
						initialDomain={domain}
					/>
				</>
			)}
		</PageLayout>
	);
};

export default DomainDetailsLayout;
